/**
 * Named salt combinations for Stage 3 (Nutrient Salt Management).
 * Persisted in localStorage so users can reload favorite sets without re-picking.
 */

export interface SaltCombinationSnapshot {
  id: string;
  name: string;
  formula?: string;
}

export interface SaltCombination {
  id: string;
  name: string;
  description?: string;
  /** Salt IDs from library at save time */
  saltIds: string[];
  /** Display snapshots if a salt is later deleted from DB */
  saltSnapshots: SaltCombinationSnapshot[];
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'flahafast.saltCombinations.v1';

function readAll(): SaltCombination[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(list: SaltCombination[]): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // quota / private mode
  }
}

function newId(): string {
  return `combo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function listSaltCombinations(): SaltCombination[] {
  return readAll().sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function getSaltCombination(id: string): SaltCombination | undefined {
  return readAll().find((c) => c.id === id);
}

export function saveSaltCombination(input: {
  id?: string;
  name: string;
  description?: string;
  saltIds: string[];
  saltSnapshots?: SaltCombinationSnapshot[];
}): SaltCombination {
  const name = String(input.name || '').trim();
  if (!name) throw new Error('Combination name is required');
  if (!input.saltIds?.length) throw new Error('Select at least one salt');

  const list = readAll();
  const now = new Date().toISOString();
  const existingIdx = input.id ? list.findIndex((c) => c.id === input.id) : -1;

  // Unique name (case-insensitive) except when updating same id
  const nameTaken = list.some(
    (c) =>
      c.name.toLowerCase() === name.toLowerCase() &&
      c.id !== input.id
  );
  if (nameTaken) throw new Error(`A combination named "${name}" already exists`);

  const combo: SaltCombination = {
    id: existingIdx >= 0 ? list[existingIdx].id : newId(),
    name,
    description: input.description?.trim() || undefined,
    saltIds: [...new Set(input.saltIds)],
    saltSnapshots: input.saltSnapshots || list[existingIdx]?.saltSnapshots || [],
    createdAt: existingIdx >= 0 ? list[existingIdx].createdAt : now,
    updatedAt: now,
  };

  if (existingIdx >= 0) list[existingIdx] = combo;
  else list.unshift(combo);
  writeAll(list);
  return combo;
}

export function deleteSaltCombination(id: string): boolean {
  const list = readAll();
  const next = list.filter((c) => c.id !== id);
  if (next.length === list.length) return false;
  writeAll(next);
  return true;
}

export function findCombinationByName(name: string): SaltCombination | undefined {
  const n = name.trim().toLowerCase();
  return readAll().find((c) => c.name.toLowerCase() === n);
}
